/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React from "react";
import { Box, HStack, VStack, Text, Badge, Icon, Divider } from "@chakra-ui/react";
import { useTheme } from "../hooks/useTheme";

/**
 * Unified Card Component
 * Provides consistent card styling across all admin dashboard pages
 */
export default function UnifiedCard({
  children,
  icon: IconComponent,
  title,
  subtitle,
  badge,
  badgeColor,
  onClick,
  hoverable = true,
  ...props
}) {
  const { colors } = useTheme();

  return (
    <Box
      bg={colors.cardBg}
      borderRadius="xl"
      boxShadow="md"
      p={{ base: 4, md: 5, lg: 6 }}
      borderWidth="1px"
      borderColor={colors.borderColor}
      transition="all 0.3s ease"
      cursor={onClick ? "pointer" : "default"}
      w="100%"
      onClick={onClick}
      _hover={
        hoverable
          ? {
              transform: "translateY(-5px)",
              boxShadow: "xl",
              borderColor: colors.mainColor,
            }
          : {}
      }
      {...props}
    >
      {(title || IconComponent || badge) && (
        <HStack justify="space-between" mb={3} align="flex-start">
          {(title || IconComponent) && (
            <HStack spacing={3} flex={1}>
              {IconComponent && (
                <Icon
                  as={IconComponent}
                  color={colors.mainColor}
                  boxSize={6}
                />
              )}
              {title && (
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }} color={colors.textColor}>
                    {title}
                  </Text>
                  {subtitle && (
                    <Text fontSize={{ base: "xs", md: "sm" }} color={colors.subText}>
                      {subtitle}
                    </Text>
                  )}
                </VStack>
              )}
            </HStack>
          )}
          {badge && (
            <Badge
              bg={badgeColor || colors.mainColorLight}
              color={badgeColor ? colors.textColor : colors.mainColor}
              px={3}
              py={1}
              borderRadius="md"
              fontWeight="semibold"
            >
              {badge}
            </Badge>
          )}
        </HStack>
      )}

      {children && (
        <>
          {(title || IconComponent || badge) && (
            <Divider my={3} borderColor={colors.dividerColor} />
          )}
          <Box>{children}</Box>
        </>
      )}
    </Box>
  );
}

